import React, { useEffect, useState } from 'react';
import { Typography, Steps, Skeleton } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useHistory, useParams } from 'react-router-dom';
import get from 'lodash/get';
import assign from 'lodash/assign';
import ContentPageHeader from '../../../components/contentPageHeader';
import ProductDetailsForm from './ProductDetailsForm';
import AdditionalProductInformationForm from './AdditionalProductInformationForm';
import ProductOtherDetailsForm from './ProductOtherDetailsForm';
import ProductFinalDetails from './ProductFinalDetails';
import ProductInfoForm from './ProductInfoForm';
import requireAuth from '../../../hocs/requireAuth';
import { productMangement } from '../../../redux/actions/productAction';
import { EDIT_PRODUCT } from '../../../constants/routes';
import { generateInitialEditpaylod } from '../../../utilities/product';
import { hasAnyPermission } from '../../../utilities/helpers';
import {
  ROLE_MARKETING_ADMIN,
  ROLE_MARKETING_MANAGER,
} from '../../../constants/roles';

const { Text } = Typography;

function CreateProduct() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const { companyId, id } = useParams();
  const edit = !!matchPath(history.location.pathname, {
    path: EDIT_PRODUCT,
    exact: true,
  });
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const role = useSelector((state) => get(state, 'auth.role'));

  const [payload, setPayload] = useState({
    step: 0,
    step1: null,
    step2: null,
    step3: null,
    step4: null,
  });

  const isMarketingUser = hasAnyPermission(
    [ROLE_MARKETING_ADMIN, ROLE_MARKETING_MANAGER],
    [get(role, 'role_code')]
  );

  const payloadCompanyId = isMarketingUser
    ? get(payload, 'step1.company_id')
    : get(selectedCompany, 'id');

  const onSubmitPayload = (currentStep, obj = {}) => {
    let key = null;
    switch (currentStep) {
      case 0:
        key = 'step1';
        break;
      case 1:
        key = 'step2';
        break;
      case 2:
        key = 'step3';
        break;
      case 3:
        key = 'step4';
        break;
      default:
        key = null;
        break;
    }

    assign(payload, { [key]: obj, step: currentStep + 1 });
    setPayload({ ...payload });
  };

  const onMove = (step) => {
    setPayload({ ...payload, step });
  };

  const fetchProductDetails = () => {
    setLoading(true);
    dispatch(
      productMangement('get.product', {
        id: parseInt(id),
        company_id: parseInt(companyId),
      })
    )
      .then((result) => {
        const editPayload = generateInitialEditpaylod(get(result, 'data'));
        setPayload({
          ...editPayload,
          step1: {
            ...get(editPayload, 'step1'),
            is_sample: get(editPayload, 'step1.is_sample') ? 1 : 0,
          },
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (edit) {
      fetchProductDetails();
    }
  }, []);

  return (
    <div>
      <div>
        <ContentPageHeader
          title={[
            <Text className="content-page-header-heading">Product</Text>,
            <RightOutlined className="content-page-header-heading" />,
            <Text className="content-page-header-heading">Catalogue</Text>,
            <RightOutlined className="content-page-header-heading" />,
            <Text className="content-page-header-heading">
              {edit ? 'Edit Product Details' : 'Add New Product'}
            </Text>,
          ]}
          extra={[]}
        />
        <div className="create-product-stepper">
          <Steps
            labelPlacement="vertical"
            size="small"
            current={get(payload, 'step')}
            onChange={(value) => {
              setPayload({
                ...payload,
                step:
                  get(payload, 'step') > value ? value : get(payload, 'step'),
              });
            }}
            items={[
              {
                title: 'Product details',
              },
              {
                title: 'Other details',
              },
              {
                title: 'Additional Product Information',
              },
              {
                title: 'More Info',
              },
              {
                title: 'Final Confirmation',
              },
            ]}
          />
        </div>
        {(!edit || (edit && get(payload, 'step1'))) && (
          <div className="create-product-form-container">
            <Skeleton loading={loading}>
              {get(payload, 'step') === 0 && (
                <ProductDetailsForm
                  edit={edit}
                  onSubmitPayload={onSubmitPayload}
                  currentStep={get(payload, 'step')}
                  initialValues={get(payload, 'step1')}
                  isMarketingUser={isMarketingUser}
                />
              )}
              {get(payload, 'step') === 1 && (
                <ProductOtherDetailsForm
                  edit={edit}
                  onMove={onMove}
                  onSubmitPayload={onSubmitPayload}
                  companyId={payloadCompanyId}
                  isSampleProduct={get(payload, 'step1.is_sample')}
                  currentStep={get(payload, 'step')}
                  initialValues={get(payload, 'step2')}
                />
              )}
              {get(payload, 'step') === 2 && (
                <AdditionalProductInformationForm
                  edit={edit}
                  onMove={onMove}
                  onSubmitPayload={onSubmitPayload}
                  companyId={payloadCompanyId}
                  currentStep={get(payload, 'step')}
                  initialValues={get(payload, 'step3')}
                />
              )}
              {get(payload, 'step') === 3 && (
                <ProductInfoForm
                  edit={edit}
                  onMove={onMove}
                  onSubmitPayload={onSubmitPayload}
                  currentStep={get(payload, 'step')}
                  initialValues={get(payload, 'step4')}
                />
              )}
              {get(payload, 'step') === 4 && (
                <ProductFinalDetails
                  edit={edit}
                  details={payload}
                  onMove={onMove}
                  productId={id}
                  companyId={payloadCompanyId}
                />
              )}
            </Skeleton>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(CreateProduct, ['PERM_ADD_PRODUCT_CATALOG']);
